import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbWaveSquare } from "solid-icons/tb";
import { createMemo, For, Show } from "solid-js";
import { safeParseBlockJson } from "../../message/parseBlockContent";
import { useChatRuntime } from "../../runtime/ChatRuntime";
import { formatRelativeTime } from "../../util/dateFormat";
import {
  type LiveSessionSearchResultsPayload,
  parseLiveSessionSearchResultsPayload,
} from "./liveSessionSearchResultsSchema";

interface LiveSessionSearchResultsRendererProps {
  block: StreamingBlock;
}

export const LiveSessionSearchResultsRenderer = (props: LiveSessionSearchResultsRendererProps) => {
  const { t } = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo<LiveSessionSearchResultsPayload | null>(() => {
    const raw = rawContent();
    if (!raw) return null;
    const json = safeParseBlockJson(raw);
    if (!json) return null;
    return parseLiveSessionSearchResultsPayload(json);
  });

  const parseFailed = () => !!rawContent() && payload() === null;

  return (
    <Show
      when={!parseFailed()}
      fallback={
        <div class="rounded-lg border border-primary-border bg-panel px-3 py-2 text-xs text-muted-text">
          {t("live_session_search_card.parse_error")}
        </div>
      }
    >
      <Show when={payload()}>
        {(data) => (
          // An agent fans these out several per turn, so a miss draws nothing.
          <Show when={data().hits.length > 0 || data().userError}>
            <div class="overflow-hidden rounded-lg border border-primary-border bg-panel">
              <div class="flex items-center gap-2 border-b border-primary-border px-3 py-2">
                <TbWaveSquare class="size-4 shrink-0 text-brand" />
                <span class="text-sm font-medium text-primary-text">{t("live_session_search_card.title")}</span>
                <Show when={!data().userError}>
                  <span class="ml-auto shrink-0 text-xs text-muted-text">
                    {data().hits.length === 1
                      ? t("live_session_search_card.results_one")
                      : t("live_session_search_card.results", { count: String(data().hits.length) })}
                  </span>
                </Show>
              </div>

              <Show
                when={!data().userError}
                fallback={<div class="p-3 text-sm text-muted-text">{data().userError}</div>}
              >
                <ul class="divide-y divide-primary-border">
                  <For each={data().hits}>
                    {(hit) => (
                      <li class="px-3 py-2">
                        <div class="flex items-baseline justify-between gap-3">
                          <h3 class="min-w-0 truncate text-sm font-bold text-primary-text">{hit.title}</h3>
                          <span class="shrink-0 text-xs text-muted-text">{formatRelativeTime(hit.createdAt)}</span>
                        </div>
                        <Show when={hit.summary}>
                          <p class="mt-1 line-clamp-2 text-sm wrap-break-word text-secondary-text">{hit.summary}</p>
                        </Show>
                      </li>
                    )}
                  </For>
                </ul>
              </Show>
            </div>
          </Show>
        )}
      </Show>
    </Show>
  );
};
